import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Shield, ArrowRight, Play, Check, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { getKnockout, createSemifinal, createFinal } from "../lib/api";
import { toast } from "sonner";

const LegMatchCard = ({ match, title, onNavigate }) => {
  if (!match) return null;
  
  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {title}
        </span>
        {match.date && (
          <span className="text-xs text-muted-foreground">
            {match.date} {match.time && `• ${match.time}`}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        {/* Team 1 */}
        <div className="flex items-center gap-2 flex-1">
          {match.team1_logo ? (
            <img src={match.team1_logo} alt="" className="w-8 h-8 rounded object-cover" />
          ) : (
            <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
              <Shield className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium text-sm truncate">{match.team1_name || "TBD"}</span>
        </div>

        {/* Score */}
        <div className="flex items-center gap-2 px-3">
          {match.status === "completed" ? (
            <span className="font-bold text-lg">
              {match.team1_score} - {match.team2_score}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">vs</span>
          )}
        </div>

        {/* Team 2 */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          <span className="font-medium text-sm truncate">{match.team2_name || "TBD"}</span>
          {match.team2_logo ? (
            <img src={match.team2_logo} alt="" className="w-8 h-8 rounded object-cover" />
          ) : (
            <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
              <Shield className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {match.match_id && onNavigate && (
        <Button
          onClick={() => onNavigate(match.match_id)}
          size="sm"
          className="w-full mt-3"
          variant={match.status === "completed" ? "outline" : "default"}
          data-testid={`play-${title.toLowerCase().replace(/\s+/g, "-")}-btn`}
        >
          <Play className="w-3 h-3 mr-1" />
          {match.status === "completed" ? "View" : "Enter Score"}
        </Button>
      )}
    </div>
  );
};

const TwoLegMatchup = ({ leg1, leg2, aggregate, title, icon: Icon, iconColor, onNavigate, showButtons }) => {
  const winner = aggregate?.winner_name;
  
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${iconColor || "text-primary"}`} />}
          <span className="font-bold uppercase tracking-wider text-sm">{title}</span>
        </div>
        {aggregate && (
          <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full">
            Agg: {aggregate.team_a_aggregate} - {aggregate.team_b_aggregate}
          </span>
        )}
      </div>

      {/* Team Names Header */}
      {leg1 && (
        <div className="flex items-center justify-between mb-3 px-2">
          <span className="font-semibold">{leg1.team1_name || aggregate?.team_a_name || "TBD"}</span>
          <span className="text-muted-foreground">vs</span>
          <span className="font-semibold">{leg1.team2_name || aggregate?.team_b_name || "TBD"}</span>
        </div>
      )}

      <div className="space-y-3">
        <LegMatchCard 
          match={leg1} 
          title="Leg 1" 
          onNavigate={showButtons ? onNavigate : null} 
        />
        <LegMatchCard 
          match={leg2} 
          title="Leg 2" 
          onNavigate={showButtons ? onNavigate : null} 
        />
      </div>

      {/* Aggregate Winner */}
      {winner && (
        <div className="mt-4 p-3 bg-primary/10 border border-primary/30 rounded-lg text-center">
          <div className="flex items-center justify-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="font-bold text-primary">{winner}</span>
            <span className="text-sm text-muted-foreground">advances</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Knockout() {
  const navigate = useNavigate();
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchKnockout();
  }, []);

  const fetchKnockout = async () => {
    try {
      const response = await getKnockout();
      setBracket(response.data);
    } catch (error) {
      toast.error("Failed to fetch knockout bracket");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (matchId) => {
    navigate(`/dashboard/matches/${matchId}`);
  };

  const handleCreateFinal = async () => {
    setCreating(true);
    try {
      await createFinal();
      toast.success("Final match created");
      fetchKnockout();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create final");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!bracket) return null;

  const sf1Complete = bracket.semifinal1_leg1?.status === "completed" && bracket.semifinal1_leg2?.status === "completed";
  const sf2Complete = bracket.semifinal2_leg1?.status === "completed" && bracket.semifinal2_leg2?.status === "completed";
  const canCreateFinal = sf1Complete && sf2Complete && !bracket.final?.match_id;

  return (
    <div data-testid="knockout-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
          Knockout Stage
        </h1>
        <p className="text-muted-foreground mt-2">
          Two-leg semi-finals, final, and President's Philanthropic Cup
        </p>
      </div>

      {/* League Status */}
      <div
        className={`p-4 rounded-xl border ${
          bracket.league_complete
            ? "bg-primary/10 border-primary/30"
            : "bg-amber-500/10 border-amber-500/30"
        }`}
      >
        <div className="flex items-center gap-3">
          {bracket.league_complete ? (
            <Check className="w-5 h-5 text-primary" />
          ) : (
            <Trophy className="w-5 h-5 text-amber-500" />
          )}
          <span>
            {bracket.league_complete
              ? "League phase complete! Knockout matches are ready."
              : "League phase in progress. Complete all 45 league matches to unlock knockout stage."}
          </span>
        </div>
      </div>

      {/* Main Championship Bracket */}
      <div className="space-y-6">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-wider flex items-center gap-2">
          <Trophy className="w-6 h-6 text-amber-500" />
          Championship Bracket
        </h2>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* Semi-Finals Column */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-center text-muted-foreground">
              Semi-Finals (Home & Away)
            </h3>

            <TwoLegMatchup
              leg1={bracket.semifinal1_leg1}
              leg2={bracket.semifinal1_leg2}
              aggregate={bracket.semifinal1_aggregate}
              title="Semi-Final 1 (1st vs 4th)"
              icon={Shield}
              iconColor="text-blue-500"
              onNavigate={handleNavigate}
              showButtons={bracket.league_complete}
            />

            <TwoLegMatchup
              leg1={bracket.semifinal2_leg1}
              leg2={bracket.semifinal2_leg2}
              aggregate={bracket.semifinal2_aggregate}
              title="Semi-Final 2 (2nd vs 3rd)"
              icon={Shield}
              iconColor="text-green-500"
              onNavigate={handleNavigate}
              showButtons={bracket.league_complete}
            />
          </div>

          {/* Arrow */}
          <div className="hidden lg:flex items-center justify-center">
            <ArrowRight className="w-12 h-12 text-muted-foreground" />
          </div>

          {/* Final Column */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-center text-muted-foreground">
              Championship Final
            </h3>

            <div className="relative">
              {/* Trophy decoration */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center border-2 border-amber-500/50 z-10">
                <Trophy className="w-8 h-8 text-amber-500" />
              </div>

              <div className="pt-10 bg-card border border-border rounded-xl p-5">
                <div className="text-center mb-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-amber-500">
                    Grand Final
                  </span>
                </div>

                {bracket.final?.team1_name && bracket.final?.team2_name ? (
                  <>
                    <div className="flex items-center justify-between gap-4 p-4 bg-background rounded-lg">
                      <div className="text-center flex-1">
                        <p className="font-bold">{bracket.final.team1_name}</p>
                        {bracket.final.status === "completed" && (
                          <p className="text-2xl font-bold mt-1">{bracket.final.team1_score}</p>
                        )}
                      </div>
                      <span className="text-muted-foreground font-bold">vs</span>
                      <div className="text-center flex-1">
                        <p className="font-bold">{bracket.final.team2_name}</p>
                        {bracket.final.status === "completed" && (
                          <p className="text-2xl font-bold mt-1">{bracket.final.team2_score}</p>
                        )}
                      </div>
                    </div>

                    {bracket.final.match_id && (
                      <Button
                        onClick={() => handleNavigate(bracket.final.match_id)}
                        className="w-full mt-4"
                        data-testid="play-final-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {bracket.final.status === "completed" ? "View Result" : "Enter Score"}
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">
                      {sf1Complete && sf2Complete
                        ? "Semi-finals complete! Create the final match."
                        : "Waiting for semi-finals to complete..."}
                    </p>
                    {canCreateFinal && (
                      <Button onClick={handleCreateFinal} disabled={creating}>
                        Create Final Match
                      </Button>
                    )}
                  </div>
                )}

                {/* Champion Display */}
                {bracket.final?.winner_id && (
                  <div className="mt-4 p-4 bg-amber-500/20 border border-amber-500/50 rounded-lg text-center animate-fade-in">
                    <Trophy className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                    <h3 className="font-heading text-xl font-bold uppercase text-amber-500">
                      🏆 CHAMPIONS! 🏆
                    </h3>
                    <p className="text-lg font-bold mt-1">
                      {bracket.final.winner_id === bracket.final.team1_id
                        ? bracket.final.team1_name
                        : bracket.final.team2_name}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3rd Place Match */}
      <div className="space-y-6 pt-8 border-t border-border">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-wider flex items-center gap-2">
          <Award className="w-6 h-6 text-amber-600" />
          3rd Place Match
        </h2>
        <p className="text-muted-foreground">
          Semi-final losers compete for 3rd place. Automatically filled after both semi-final legs are complete.
        </p>

        <div className="max-w-xl">
          {bracket.third_place?.team1_name && bracket.third_place?.team2_name ? (
            <div className="bg-card border border-amber-600/30 rounded-xl p-5">
              <div className="text-center mb-3">
                <span className="text-xs font-bold uppercase tracking-widest text-amber-600">
                  3rd Place — {bracket.third_place.date || "May 3, 2026"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 p-4 bg-background rounded-lg">
                <div className="text-center flex-1">
                  <p className="font-bold">{bracket.third_place.team1_name}</p>
                  {bracket.third_place.status === "completed" && (
                    <p className="text-2xl font-bold mt-1">{bracket.third_place.team1_score}</p>
                  )}
                </div>
                <span className="text-muted-foreground font-bold">vs</span>
                <div className="text-center flex-1">
                  <p className="font-bold">{bracket.third_place.team2_name}</p>
                  {bracket.third_place.status === "completed" && (
                    <p className="text-2xl font-bold mt-1">{bracket.third_place.team2_score}</p>
                  )}
                </div>
              </div>
              {bracket.third_place.match_id && (
                <Button
                  onClick={() => handleNavigate(bracket.third_place.match_id)}
                  className="w-full mt-4"
                  data-testid="play-third-place-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {bracket.third_place.status === "completed" ? "View Result" : "Enter Score"}
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <p className="text-muted-foreground">
                Waiting for both semi-final legs to complete...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* President's Philanthropic Cup Section */}
      <div className="space-y-6 pt-8 border-t border-border">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-wider flex items-center gap-2">
          <Award className="w-6 h-6 text-purple-500" />
          President's Philanthropic Cup
        </h2>
        <p className="text-muted-foreground">
          Consolation tournament for 5th and 6th place teams. Two-leg aggregate decides the winner.
        </p>

        <div className="max-w-xl">
          <TwoLegMatchup
            leg1={bracket.presidents_cup_leg1}
            leg2={bracket.presidents_cup_leg2}
            aggregate={bracket.presidents_cup_aggregate}
            title="President's Cup Final"
            icon={Award}
            iconColor="text-purple-500"
            onNavigate={handleNavigate}
            showButtons={bracket.league_complete}
          />

          {/* PST Winner Display */}
          {bracket.presidents_cup_aggregate?.winner_name && (
            <div className="mt-4 p-4 bg-purple-500/20 border border-purple-500/50 rounded-lg text-center">
              <Award className="w-10 h-10 text-purple-500 mx-auto mb-2" />
              <h3 className="font-heading text-xl font-bold uppercase text-purple-500">
                President's Cup Winners!
              </h3>
              <p className="text-lg font-bold mt-1">
                {bracket.presidents_cup_aggregate.winner_name}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
